import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, DollarSign, Calendar } from 'lucide-react';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
}

interface PayrollEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: PayrollEntry | null;
  onSave: (entryId: string, updates: any) => Promise<void>;
  onDelete?: (entryId: string) => Promise<void>;
}

interface EditFormData {
  // Timeclock fields
  clockIn: string;
  clockOut: string;
  breakStart: string;
  breakEnd: string;
  hourlyRate: number;
  
  // Flat rate fields
  hours: number;
  flatRateAmount: number;
  
  // Commission fields
  earnings: number;
  
  // Common fields
  notes: string;
  itemName: string;
  itemDescription: string;
}

export default function PayrollEditModal({ isOpen, onClose, entry, onSave, onDelete }: PayrollEditModalProps) {
  const [formData, setFormData] = useState<EditFormData>({
    clockIn: '',
    clockOut: '',
    breakStart: '',
    breakEnd: '',
    hourlyRate: 0,
    hours: 0,
    flatRateAmount: 0,
    earnings: 0,
    notes: '',
    itemName: '',
    itemDescription: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Helper function to convert UTC timestamp to local timezone for form inputs
  const formatDateTimeForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    // Convert to local timezone for the form input
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 16);
  };

  // Helper function to convert form datetime to UTC
  const convertToUTC = (dateTimeString: string): string | null => {
    if (!dateTimeString) return null;
    const localDate = new Date(dateTimeString);
    return localDate.toISOString();
  };

  // Initialize form data when entry changes
  useEffect(() => {
    if (entry) {
      setFormData({
        clockIn: formatDateTimeForInput(entry.clock_in),
        clockOut: formatDateTimeForInput(entry.clock_out),
        breakStart: formatDateTimeForInput(entry.break_start),
        breakEnd: formatDateTimeForInput(entry.break_end),
        hourlyRate: entry.hourly_rate || 0,
        hours: entry.hours || 0,
        flatRateAmount: entry.flat_rate_amount || 0,
        earnings: entry.earnings || 0,
        notes: entry.notes || '',
        itemName: entry.item_name || '',
        itemDescription: entry.item_description || ''
      });
      setError(null);
    }
  }, [entry]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculate totals for preview
  const calculateTotals = () => {
    if (!entry) return { totalMinutes: 0, totalHours: 0, totalEarnings: 0 };

    if (entry.entry_type === 'timeclock') {
      if (formData.clockIn && formData.clockOut) {
        const start = new Date(formData.clockIn);
        const end = new Date(formData.clockOut);
        let totalMs = end.getTime() - start.getTime();
        
        // Subtract break time if applicable
        if (formData.breakStart && formData.breakEnd) {
          const breakStart = new Date(formData.breakStart);
          const breakEnd = new Date(formData.breakEnd);
          totalMs -= (breakEnd.getTime() - breakStart.getTime());
        }
        
        const totalMinutes = Math.max(0, Math.floor(totalMs / (1000 * 60)));
        const totalHours = totalMinutes / 60;
        const totalEarnings = totalHours * formData.hourlyRate;
        
        return { totalMinutes, totalHours, totalEarnings };
      }
    } else if (entry.entry_type === 'flat_rate') {
      const totalEarnings = formData.hours * formData.flatRateAmount;
      return { totalMinutes: formData.hours * 60, totalHours: formData.hours, totalEarnings };
    } else if (entry.entry_type === 'commission') {
      return { totalMinutes: 0, totalHours: 0, totalEarnings: formData.earnings };
    }
    
    return { totalMinutes: 0, totalHours: 0, totalEarnings: 0 };
  };

  const { totalMinutes, totalHours, totalEarnings } = calculateTotals();

  // Handle save
  const handleSave = async () => {
    if (!entry) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const updates: any = {
        notes: formData.notes || null,
        updated_at: new Date().toISOString()
      };

      if (entry.entry_type === 'timeclock') {
        updates.clock_in = convertToUTC(formData.clockIn);
        updates.clock_out = convertToUTC(formData.clockOut);
        updates.break_start = convertToUTC(formData.breakStart);
        updates.break_end = convertToUTC(formData.breakEnd);
        updates.rate_amount = formData.hourlyRate;
        updates.total_minutes = totalMinutes;
        updates.hours = totalHours;
        updates.total_earnings = totalEarnings;
      } else if (entry.entry_type === 'flat_rate') {
        updates.hours = formData.hours;
        updates.rate_amount = formData.flatRateAmount;
        updates.total_earnings = totalEarnings;
        updates.item_name = formData.itemName || null;
        updates.item_description = formData.itemDescription || null;
      } else if (entry.entry_type === 'commission') {
        updates.total_earnings = formData.earnings;
        updates.earnings = formData.earnings;
        updates.item_name = formData.itemName || null;
        updates.item_description = formData.itemDescription || null;
      }

      await onSave(entry.id, updates);
      onClose();
    } catch (err) {
      console.error('Error saving payroll entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!entry || !onDelete) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onDelete(entry.id);
      setShowDeleteConfirmation(false);
      onClose();
    } catch (err) {
      console.error('Error deleting payroll entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !entry) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Edit Payroll Entry</h3>
              <p className="text-sm text-gray-600 mt-1">
                {entry.entry_type === 'timeclock' && 'Modify time clock entry'}
                {entry.entry_type === 'flat_rate' && 'Modify flat rate entry'}
                {entry.entry_type === 'commission' && 'Modify commission entry'}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <div className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  {entry.entry_type.replace('_', ' ').toUpperCase()}
                </div>
                <span className="text-xs text-gray-500">
                  {entry.resource_first_name} {entry.resource_last_name}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Form Content */}
        <div className="p-4 sm:p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Timeclock Entry Fields */}
          {entry.entry_type === 'timeclock' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Clock In
                  </label>
                  <input
                    type="datetime-local"
                    name="clockIn"
                    value={formData.clockIn}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Clock Out
                  </label>
                  <input
                    type="datetime-local"
                    name="clockOut"
                    value={formData.clockOut}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Break Start</label>
                  <input
                    type="datetime-local"
                    name="breakStart"
                    value={formData.breakStart}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Break End</label>
                  <input
                    type="datetime-local"
                    name="breakEnd"
                    value={formData.breakEnd}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  Hourly Rate
                </label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  step="0.01"
                  min="0"
                />
              </div>
            </>
          )}

          {/* Flat Rate Entry Fields */}
          {entry.entry_type === 'flat_rate' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
                <input
                  type="text"
                  name="itemName"
                  value={formData.itemName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Description</label>
                <input
                  type="text"
                  name="itemDescription"
                  value={formData.itemDescription}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Hours
                  </label>
                  <input
                    type="number"
                    name="hours"
                    value={formData.hours}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                    step="0.1"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <DollarSign className="h-4 w-4 inline mr-1" />
                    Rate per Hour
                  </label>
                  <input
                    type="number"
                    name="flatRateAmount"
                    value={formData.flatRateAmount}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </>
          )}

          {/* Commission Entry Fields */}
          {entry.entry_type === 'commission' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
                <input
                  type="text"
                  name="itemName"
                  value={formData.itemName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Description</label>
                <input
                  type="text"
                  name="itemDescription"
                  value={formData.itemDescription}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  Commission Amount
                </label>
                <input
                  type="number"
                  name="earnings"
                  value={formData.earnings}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  step="0.01"
                  min="0"
                />
              </div>
            </>
          )}

          {/* Notes field for all types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
              placeholder="Optional notes..."
            />
          </div>

          {/* Live calculation preview */}
          {(entry.entry_type === 'timeclock' || entry.entry_type === 'flat_rate') && (
            <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Calculated Totals</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {entry.entry_type === 'timeclock' && (
                  <div>
                    <span className="text-gray-500">Total Time:</span>
                    <div className="font-medium">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</div>
                  </div>
                )}
                {entry.entry_type === 'flat_rate' && (
                  <div>
                    <span className="text-gray-500">Hours:</span>
                    <div className="font-medium">{totalHours.toFixed(1)}h</div>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Total Earnings:</span>
                  <div className="font-bold text-green-600">${totalEarnings.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
          {!showDeleteConfirmation ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              {entry.entry_type === 'flat_rate' && onDelete && (
                <button
                  type="button"
                  className="px-6 py-3 text-sm font-medium text-red-600 bg-red-50/60 backdrop-blur-sm border border-red-200 rounded-xl hover:bg-red-100/80 transition-all duration-150 w-full sm:w-auto"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={isSubmitting}
                >
                  Delete Entry
                </button>
              )}
              <button
                type="button"
                className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="h-4 w-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          ) : (
            /* Delete Confirmation */
            <div className="space-y-4">
              <div className="bg-red-50/70 backdrop-blur-sm border border-red-200/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-red-900">Delete Payroll Entry</h3>
                    <p className="text-sm text-red-700 mt-1">
                      Are you sure you want to delete this {entry.entry_type === 'flat_rate' ? 'flat rate' : ''} entry? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
                  onClick={() => setShowDeleteConfirmation(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <div className="flex items-center justify-center">
                      <div className="h-4 w-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Deleting...
                    </div>
                  ) : (
                    'Delete Entry'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}